/**
 * Licensed Materials - Property of IBM
 * IBM Watson (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { Link, Tooltip } from 'ca-ui-toolkit';

import VisualizationPreview from './VisualizationPreview';
import VisualizationAutoMode from './VisualizationAutoMode';
import StringResources from './../nls/StringResources';

import './visPreviewList.scss';

const CLASS_NAME = 'vis-recommender__preview-list';
@observer
class VisualizationPreviewList extends Component {

	constructor(props) {
		super(props);

		this._rootRef = React.createRef();

		this.state = {
			maxDisplayedItems: this.props.maxDisplayedItems,
			isExpanded: false
		};
	}

	render() {

		const isHorizontal = this.props.horizontal;

		const classNames = classnames(`${CLASS_NAME}`, {
			[`${CLASS_NAME}--horizontal`]: isHorizontal,
			[`${CLASS_NAME}--vertical`]: !isHorizontal
		});

		const itemsClassNames = classnames({
			[`${CLASS_NAME}--horizontal__items`]: isHorizontal,
			[`${CLASS_NAME}--vertical__items`]: !isHorizontal
		});

		return (
			<div className={classNames} ref={this._rootRef}>
				<div className={itemsClassNames}>
					{this.renderAutoMode()}
					{this.renderListItems()}
				</div>
				{this.renderSeeMore()}
			</div>
		);
	}

	_getFinalVizItemsList() {

		const { maxDisplayedItems } = this.state;
		const { visStartingIndex, items } = this.props;

		if (maxDisplayedItems > -1) {
			return items.slice(
				visStartingIndex,
				visStartingIndex + maxDisplayedItems
			);
		} else {
			return items;
		}
	}

	renderAutoMode() {
		if(this.props.selectedVisId) {
			const isSelected = this.props.isVizTypeLocked !== true;
			const isHorizontal = this.props.horizontal;
			return (
				<Tooltip
					title={StringResources.get('change_vis')}
					orient='top'
					display='block'
				>
					<VisualizationAutoMode
						isHorizontal={isHorizontal}
						isSelected={isSelected}
						onSelect={this._onAutoSwitchIconClick}
						size={this.props.itemSize}
					/>
				</Tooltip>
			);
		}
	}

	_onAutoSwitchIconClick = () => {
		// TODO: have a separate argument for this (onAutoClick or smth like that)
		this.props.onClickItem({
			key: 'autoModeIcon',
			isAutoModeSwitcher: true
		}, 0);
	}

	renderListItems() {

		const {
			horizontal,
			isVizTypeLocked,
			visStartingIndex,
			predictData,
			isPredictPreview,
			onAddItem,
			titleOnBottom,
			onClickItem,
			spinnerSize,
			featureSet,
			onPromptingError,
			isNewLayoutEnabled,
			additionalProperties,
			noBottomTitle,
			itemSize,

			// TODO: to be used from context
			dashboardApi,
			logger
		} = this.props;

		const items = this._getFinalVizItemsList();

		return items.map((item, i) => {
			const index = visStartingIndex + i;

			// when new recommendations are coming in, if we don't make each key unique,
			// react will not pick up on the change and keep the old view without re-rendering
			// mainly because we don't do any processing of `item` in the VisualizationPreview component.
			// the proper way to do this would be to handle the new `item` prop through componentDidUpdate
			// and not care about the key.
			// for now we can live with this.
			const key = index + item.name;
			return (
				<VisualizationPreview
					isVizTypeLocked={isVizTypeLocked}
					horizontal={horizontal}
					key={key}
					visTypeName={item}
					predictData={predictData}
					isPredictPreview={isPredictPreview}
					index={index}
					item={item}
					add={onAddItem}
					titleOnBottom={titleOnBottom}
					onClick={onClickItem}
					dashboardApi={dashboardApi}
					spinnerSize={spinnerSize}
					featureSet={featureSet}
					logger={logger}
					onPromptingError={onPromptingError}
					isNewLayoutEnabled={isNewLayoutEnabled}
					additionalProperties={additionalProperties}
					noBottomTitle={noBottomTitle}
					size={itemSize}
				/>
			);
		});
	}

	renderSeeMore() {
		const classNames = classnames(
			`${CLASS_NAME}__see-more`,
			{
				[`${CLASS_NAME}__see-more--hidden`]: !this._isSeeMoreEnabled()
			}
		);

		return (
			<div className={classNames}>
				<Link
					tabIndex="0"
					href="#"
					target="_self"
					aria-label={StringResources.get('see_more_aria_label')}
					onClick={this._onClickSeeMore.bind(this)}
					onKeyDown={this._onKeyDownSeeMore.bind(this)}
				>
					{StringResources.get('see_more')}
				</Link>
			</div>
		);
	}

	_onKeyDownSeeMore(e) {
		if (e.keyCode === 13 || e.keyCode === 32) {
			this._doSeeMore(e);
		}
	}

	_onClickSeeMore(e) {
		this._doSeeMore(e);
	}

	_doSeeMore(e) {
		e.preventDefault();

		this.setState({
			maxDisplayedItems: -1,
			isExpanded: true
		});
	}

	_isSeeMoreEnabled() {
		if (this.props.enableSeeMore && !this.state.isExpanded) {
			return this.props.items.length > 3;
		}
		return false;
	}

	componentDidMount() {
		if (this._rootRef.current && this.props.horizontal && this.props.hasFocus) {
			this._rootRef.current.querySelector('[tabIndex]').focus();
		}
	}
}

VisualizationPreviewList.propTypes = {
	dashboardApi: PropTypes.object.isRequired,
	items: PropTypes.array.isRequired,
	onClickItem: PropTypes.func,
	onAddItem: PropTypes.func,
	horizontal: PropTypes.bool,
	maxDisplayedItems: PropTypes.number,
	visStartingIndex: PropTypes.number,
	titleOnBottom: PropTypes.bool,
	enableSeeMore: PropTypes.bool,
	recommendSwitch: PropTypes.bool,
	isVizTypeLocked: PropTypes.bool,
	selectedVisId: PropTypes.string,
	additionalProperties: PropTypes.arrayOf(PropTypes.object),
	spinnerSize: PropTypes.oneOf(['small', 'normal', 'large']),
	featureSet: PropTypes.array,
	predictData: PropTypes.object,
	logger: PropTypes.object,
	isPredictPreview: PropTypes.bool,
	onPromptingError: PropTypes.func,
	isNewLayoutEnabled: PropTypes.bool,
	noBottomTitle: PropTypes.bool,
	itemSize: PropTypes.oneOf(['small', 'medium']),
	hasFocus: PropTypes.bool
};

VisualizationPreviewList.defaultProps = {
	horizontal: false,
	maxDisplayedItems: -1,
	visStartingIndex: 0,
	titleOnBottom: true,
	enableSeeMore: true,
	recommendSwitch: false,
	isVizTypeLocked: false,
	isPredictPreview: false,
	additionalProperties: [],
	spinnerSize: 'normal',
	featureSet: [],
	onPromptingError: () => {},
	noBottomTitle: false,
	hasFocus: true
};

export default VisualizationPreviewList;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-recommender/src/js/preview/VisualizationPreviewList.js